import api from './api';

const notificationService = {
  // Get all notifications for authenticated user
  getNotifications: async (params = {}) => {
    const response = await api.get('/notifications', { params });
    return {
      notifications: response.data.notifications || [],
      pagination: response.data.pagination || {}
    };
  },

  // Get unread notification count
  getUnreadCount: async () => {
    const response = await api.get('/notifications/unread-count');
    return response.data.count || 0;
  },

  // Get notification statistics
  getStats: async () => {
    const response = await api.get('/notifications/stats');
    return response.data;
  },

  // Get notifications by type
  getByType: async (type, params = {}) => {
    const response = await api.get(`/notifications/type/${type}`, { params });
    return {
      notifications: response.data.notifications || [],
      pagination: response.data.pagination || {}
    };
  },

  // Get single notification by ID
  getById: async (id) => {
    const response = await api.get(`/notifications/${id}`);
    return response.data.notification;
  },

  // Create new notification (admin/system)
  create: async (notificationData) => {
    const response = await api.post('/notifications', notificationData);
    return response.data;
  },

  // Create bulk notifications
  createBulk: async (notifications) => {
    const response = await api.post('/notifications/bulk', { notifications });
    return response.data;
  },

  // Mark notification as read
  markAsRead: async (id) => {
    const response = await api.patch(`/notifications/${id}/read`);
    return response.data;
  },

  // Mark notification as unread
  markAsUnread: async (id) => {
    const response = await api.patch(`/notifications/${id}/unread`);
    return response.data;
  },

  // Mark all notifications as read
  markAllAsRead: async () => {
    const response = await api.patch('/notifications/mark-all-read');
    return response.data;
  },

  // Delete notification
  delete: async (id) => {
    const response = await api.delete(`/notifications/${id}`);
    return response.data;
  },

  // Delete all read notifications
  deleteAllRead: async () => {
    const response = await api.delete('/notifications/read/all');
    return response.data;
  },

  // Notification type configurations
  notificationTypes: {
    comment_added: {
      icon: '💬',
      color: 'blue',
      label: 'Comment Added'
    },
    comment_reply: {
      icon: '↩️',
      color: 'blue',
      label: 'Comment Reply'
    },
    mention: {
      icon: '@',
      color: 'purple',
      label: 'Mentioned'
    },
    task_assigned: {
      icon: '📋',
      color: 'green',
      label: 'Task Assigned'
    },
    task_completed: {
      icon: '✅',
      color: 'green',
      label: 'Task Completed'
    },
    task_overdue: {
      icon: '⚠️',
      color: 'red',
      label: 'Task Overdue'
    },
    project_status_changed: {
      icon: '🔄',
      color: 'orange',
      label: 'Project Status Changed'
    },
    milestone_reached: {
      icon: '🎯',
      color: 'green',
      label: 'Milestone Reached'
    },
    budget_exceeded: {
      icon: '💰',
      color: 'red',
      label: 'Budget Exceeded'
    },
    expense_approved: {
      icon: '✓',
      color: 'green',
      label: 'Expense Approved'
    },
    risk_identified: {
      icon: '⚡',
      color: 'red',
      label: 'Risk Identified'
    },
    issue_created: {
      icon: '🐛',
      color: 'orange',
      label: 'Issue Created'
    },
    document_uploaded: {
      icon: '📄',
      color: 'blue',
      label: 'Document Uploaded'
    },
    document_shared: {
      icon: '🔗',
      color: 'blue',
      label: 'Document Shared'
    }
  },

  // Helper: Get notification type config
  getTypeConfig: (type) => {
    return notificationService.notificationTypes[type] || {
      icon: '🔔',
      color: 'gray',
      label: 'Notification'
    };
  },

  // Helper: Format notification for display
  formatNotification: (notification) => {
    const typeConfig = notificationService.getTypeConfig(notification.type);
    
    return {
      ...notification,
      icon: typeConfig.icon,
      color: typeConfig.color,
      typeLabel: typeConfig.label,
      formattedDate: notificationService.formatDate(notification.created_at),
      timeAgo: notificationService.getTimeAgo(notification.created_at),
      isNew: !notification.is_read && notificationService.isRecent(notification.created_at)
    };
  },

  // Helper: Format date
  formatDate: (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
  },

  // Helper: Get time ago string
  getTimeAgo: (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    } else {
      return date.toLocaleDateString();
    }
  },

  // Helper: Check if notification is recent (within 1 hour)
  isRecent: (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = (now - date) / (1000 * 60);
    return diffInMinutes < 60;
  },

  // Helper: Group notifications by date
  groupByDate: (notifications) => {
    const groups = {
      today: [],
      yesterday: [],
      thisWeek: [],
      older: []
    };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    notifications.forEach(notification => {
      const notifDate = new Date(notification.created_at);
      const notifDay = new Date(notifDate.getFullYear(), notifDate.getMonth(), notifDate.getDate());

      if (notifDay.getTime() === today.getTime()) {
        groups.today.push(notification);
      } else if (notifDay.getTime() === yesterday.getTime()) {
        groups.yesterday.push(notification);
      } else if (notifDate >= weekAgo) {
        groups.thisWeek.push(notification);
      } else {
        groups.older.push(notification);
      }
    });

    return groups;
  },

  // Helper: Filter notifications
  filterNotifications: (notifications, filters = {}) => {
    let filtered = [...notifications];

    if (filters.type) {
      filtered = filtered.filter(n => n.type === filters.type);
    }

    if (filters.isRead !== undefined) {
      filtered = filtered.filter(n => n.is_read === filters.isRead);
    }

    if (filters.entityType) {
      filtered = filtered.filter(n => n.entity_type === filters.entityType);
    }

    return filtered;
  },

  // Real-time notification polling (call this periodically)
  startPolling: (callback, interval = 30000) => {
    const pollInterval = setInterval(async () => {
      try {
        const count = await notificationService.getUnreadCount();
        callback(count);
      } catch (error) {
        console.error('Error polling notifications:', error);
      }
    }, interval);

    return () => clearInterval(pollInterval);
  }
};

export default notificationService;


